import axios, { type AxiosInstance } from 'axios';
import * as signalR from '@microsoft/signalr';
import { clearDevToken } from '../auth/devSession';
import type {
  CreateJobRequest, JobSummaryDto, JobDetailDto,
  SubmitQuoteRequest, QuoteSummaryDto, QuoteCalculationDto,
  ChatbotMessageRequest, ChatbotMessageResponse,
  ContractDto, ContractSummaryDto, JobType, CarrierEarningsDashboardDto, MonthlyStatementDto, RouteResponse, LiveRouteResponse,
  RelaySegmentDto,
  UserProfileDto, UpdateProfileRequest,
  DocumentUpload, DocumentType,
  JobAlertPreference, CreateJobAlertRequest,
  TrafficIncident, HourlyWeather,
  PaymentIntentDto,
  UserVehicle, RegisterVehicleInput, UpdateVehicleInput,
  VehicleLookupResult, MotTest,
  CarrierEmployee, RegisterEmployeeInput,
  WorkshopEmployee, RegisterWorkshopEmployeeInput,
  JobAssignment,
  FavouriteJobSummary, AvailableJobsResponse,
  BazaarListing, BazaarSearchParams, BazaarQuote, BazaarBrowseResult,
  SubmitBazaarQuoteInput, BazaarOrder,
  CreateBazaarListingInput, ModerationQueueItem, BazaarFavouriteSummary,
  ClassifiedListing, ClassifiedSearchParams,
  CreateClassifiedListingInput, CreateClassifiedListingResult, ClassifiedFavouriteSummary,
  ListingMedia, BazaarAiSuggestInput, AiSuggestResult,
  CreateBazaarOrderInput, CreateBazaarOrderResult, BazaarChatMessage,
  ShippingAddressInput, ShippingRateQuote, BazaarShipment, BazaarFulfilmentMethod,
  InvoiceDto, CreateInvoiceRequest, InvoiceSettingsDto, UpdateInvoiceSettingsRequest,
  PlatformFeeRule, UpsertPlatformFeeRuleInput,
  WalletAccountDto, WalletLedgerEntryDto, WalletTopUpDto, PendingWalletTopUpDto,
  CreateWalletTopUpResponse, WalletTopUpMethod,
  DepositQuoteDto, DepositBankTransferInstructionDto,
  IdentityVerificationDto, IdentityVerificationStatusDto, IdentityVerificationType,
  MyBazaarReviewDto, BazaarSellerReviewSummary, BazaarCategory,
  ListingCategoryAttribute,
  AutoQuoteRuleDto, SaveAutoQuoteRuleRequest, AutoQuotePreviewRequest, AutoQuotePreviewDto,
  WorkshopStandardServiceDto, SaveWorkshopStandardServiceRequest,
  AvailabilityDto, AddAvailabilityRequest,
  DeliveryEstimateItem, DeliveryEstimateResult,
} from '../types';
import type {
  EarningsDashboardDto, ReferralDashboardDto,
  AdminPaymentsQueueDto, AdminReferralOverviewDto,
  PlatformKpiSnapshot, MonthlyKpiRow, DailyRevenueTick, TopCarrierRow,
  DisputeDto, DisputeSummaryDto,
  NotificationDto, NotificationPreference,
} from '../types/modules';

// ─────────────────────────────────────────────
//  API CLIENT FACTORY  (requires MSAL token)
//  Uses relative paths so the Vite proxy routes to the backend in dev.
//  In production the frontend must be served from the same origin as the API,
//  OR VITE_API_BASE_URL must be set and CORS configured on the backend.
// ─────────────────────────────────────────────
const createApiClient = (getToken: () => Promise<string>): AxiosInstance => {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use(async (config) => {
    const token = await getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err.response?.status === 401) {
        // A soft-deleted account's CIAM session is otherwise still valid (soft delete never
        // touches CIAM), so without this the app would keep showing a stale authenticated screen
        // even though every subsequent API call 401s. Mirrors mobile's clearAuth-on-401 interceptor.
        clearDevToken();
        window.location.href = '/';
      }
      const message = err.response?.data?.detail
        ?? err.response?.data?.error
        ?? err.message
        ?? 'An error occurred';
      return Promise.reject(new Error(message));
    }
  );

  return client;
};

// ─────────────────────────────────────────────
//  DEFAULT API INSTANCE  (unauthenticated / chatbot)
//  Always relative so requests go through the Vite proxy in dev.
// ─────────────────────────────────────────────
export const api = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL ?? '') + '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const message = err.response?.data?.detail
      ?? err.response?.data?.error
      ?? err.message
      ?? 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

// ─────────────────────────────────────────────
//  JOB SERVICE
// ─────────────────────────────────────────────
export const jobService = (client: AxiosInstance) => ({
  createJob: (data: CreateJobRequest) =>
    client.post<string>('/api/jobs', data).then((r) => r.data),

  saveDraft: (data: CreateJobRequest) =>
    client.post<{ jobId: string }>('/api/jobs', { ...data, saveAsDraft: true }).then((r) => r.data),

  getJob: (id: string) =>
    client.get<JobDetailDto>(`/api/jobs/${id}`).then((r) => r.data),

  getAvailableJobs: (lat: number, lng: number, radius = 25) =>
    client.get<JobSummaryDto[]>('/api/jobs/feed', { params: { lat, lng, radius } })
      .then((r) => r.data),

  attendAlerts: () =>
    client.post('/api/jobs/attend-alerts').catch(() => {}),

  getMyJobs: () =>
    client.get<JobSummaryDto[]>('/api/jobs/mine').then((r) => r.data),

  getAllJobs: () =>
    client.get<JobSummaryDto[]>('/api/jobs/all').then((r) => r.data),

  getMyQuotedJobs: () =>
    client.get<JobSummaryDto[]>('/api/v1/quotes/my-jobs').then((r) => r.data),

  startJob: (id: string) =>
    client.post(`/api/jobs/${id}/start`),

  completeJob: (id: string) =>
    client.post(`/api/jobs/${id}/complete`),

  editJob: (id: string, data: {
    title: string; description: string; isSameDay: boolean;
    scheduledAt: string; goodsDescription: string;
    estimatedValueGbp?: number | null; requiredVehicleType: number;
  }) =>
    client.put(`/api/jobs/${id}`, data),

  cancelJob: (id: string, reason: string) =>
    client.post(`/api/jobs/${id}/cancel`, { reason }),

  getDeliveryOtp: (id: string) =>
    client.get<{ otp: string; expiresInSeconds: number }>(`/api/jobs/${id}/otp`)
      .then((r) => r.data),

  submitPod: (id: string, data: {
    photoUrls: string[];
    customerSignatureUrl: string;
    recipientName: string;
    gpsLatitude: number;
    gpsLongitude: number;
    gpsAccuracyMetres: number;
    otpCode: string;
  }) =>
    client.post<{ podId: string }>(`/api/jobs/${id}/pod`, {
      photoUrl:             data.photoUrls[0] ?? '',
      customerSignatureUrl: data.customerSignatureUrl,
      recipientName:        data.recipientName,
      gpsLatitude:          data.gpsLatitude,
      gpsLongitude:         data.gpsLongitude,
      gpsAccuracyMetres:    data.gpsAccuracyMetres,
      otpCode:              data.otpCode,
    }).then((r) => r.data),

  uploadImages: (jobId: string, images: Array<{ base64: string; mimeType: string }>) =>
    client.post<{ urls: string[] }>(`/api/jobs/${jobId}/images`, { images }).then((r) => r.data),

  carrierAcceptJob: (jobId: string, vehicleId?: string, employeeId?: string) =>
    client.post<{ jobId: string; acceptedAt: string }>(
      `/api/jobs/${jobId}/carrier-accept`,
      { vehicleId: vehicleId || null, employeeId: employeeId || null }
    ).then((r) => r.data),

  createCustomerPaymentIntent: (jobId: string) =>
    client.post<PaymentIntentDto>(`/api/jobs/${jobId}/payment-intent`).then((r) => r.data),

  createCarrierCommissionIntent: (jobId: string) =>
    client.post<PaymentIntentDto>(`/api/jobs/${jobId}/carrier-payment-intent`).then((r) => r.data),

  createCustomerCheckoutUrl: (jobId: string) =>
    client.post<{ checkoutUrl: string }>(`/api/jobs/${jobId}/checkout-url`).then((r) => r.data.checkoutUrl),

  createCarrierCheckoutUrl: (jobId: string) =>
    client.post<{ checkoutUrl: string }>(`/api/jobs/${jobId}/carrier-checkout-url`).then((r) => r.data.checkoutUrl),

  publishDraft: (jobId: string) =>
    client.post(`/api/jobs/${jobId}/publish`),

  arriveAtDrop: (jobId: string, dropId: string) =>
    client.post(`/api/v1/jobs/${jobId}/drops/${dropId}/arrive`),

  completeDrop: (jobId: string, dropId: string, data: { photoUrls: string[]; latitude: number; longitude: number }) =>
    client.post(`/api/v1/jobs/${jobId}/drops/${dropId}/complete`, data),

  arriveAtPickup: (jobId: string, pickupId: string) =>
    client.post(`/api/v1/jobs/${jobId}/pickups/${pickupId}/arrive`),

  collectPickup: (jobId: string, pickupId: string, data: { photoUrls: string[]; latitude: number; longitude: number }) =>
    client.post(`/api/v1/jobs/${jobId}/pickups/${pickupId}/collect`, data),
});

// ─────────────────────────────────────────────
//  QUOTE SERVICE
// ─────────────────────────────────────────────
export const quoteService = (client: AxiosInstance) => ({
  submitQuote: (data: SubmitQuoteRequest) =>
    client.post<string>('/api/v1/quotes', data).then((r) => r.data),

  getQuotesForJob: (jobId: string) =>
    client.get<QuoteSummaryDto[]>(`/api/v1/quotes/job/${jobId}`).then((r) => r.data),

  acceptQuote: (quoteId: string, jobId: string) =>
    client.post('/api/v1/quotes/accept', { quoteId, jobId }),

  rejectQuote: (quoteId: string) =>
    client.post(`/api/v1/quotes/${quoteId}/reject`),

  withdrawQuote: (quoteId: string) =>
    client.delete(`/api/v1/quotes/${quoteId}`),

  calculateQuote: (jobId: string, amount?: number, labourAmount?: number, partsAmount?: number) =>
    client.get<QuoteCalculationDto>('/api/v1/quotes/calculate', {
      params: { jobId, amount, labourAmount, partsAmount },
    }).then((r) => r.data),
});

// ─────────────────────────────────────────────
//  RELAY SERVICE
// ─────────────────────────────────────────────
export const relayService = (client: AxiosInstance) => ({
  getSegments: (jobId: string) =>
    client.get<RelaySegmentDto[]>(`/api/relay/${jobId}/segments`).then((r) => r.data),

  assignSegment: (segmentId: string, carrierId: string) =>
    client.post('/api/relay/assign', { segmentId, carrierId }),

  completeHandover: (segmentId: string, photoUrl: string, notes: string) =>
    client.post('/api/relay/handover', { segmentId, handoverPhotoUrl: photoUrl, handoverNotes: notes }),
});

// ─────────────────────────────────────────────
//  CONTRACT SERVICE
// ─────────────────────────────────────────────
export const contractService = (client: AxiosInstance) => ({
  getContract: (jobId: string) =>
    client.get<ContractDto>(`/api/contracts/job/${jobId}`)
      .then((r) => r.data)
      .catch((e: any) => {
        // 404 = no contract generated yet; 403 = not a party to this job's contract.
        // Both are expected, non-error states for the calling page — render nothing.
        if (e?.response?.status === 404 || e?.response?.status === 403) return null;
        throw e;
      }),

  downloadPdf: (contractId: string) =>
    client.get(`/api/contracts/${contractId}/pdf`, { responseType: 'blob' })
      .then((r) => URL.createObjectURL(r.data)),

  getMyContracts: (params?: { jobType?: JobType; sort?: string; page?: number; pageSize?: number }) =>
    client.get<ContractSummaryDto[]>('/api/contracts/mine', { params }).then((r) => r.data),
});

// ─────────────────────────────────────────────
//  REVIEW SERVICE
// ─────────────────────────────────────────────
export interface ReviewDto {
  id: string;
  jobId: string;
  reviewerId: string;
  revieweeId: string;
  rating: number;
  comment: string | null;
  createdAt: string;
}

export interface SubmitReviewRequest {
  jobId: string;
  revieweeId: string;
  rating: number;
  comment?: string | null;
}

export interface MyReviewDto {
  reviewId: string;
  jobId: string;
  jobReferenceNumber: string;
  jobTitle: string;
  reviewerId: string;
  reviewerName: string;
  rating: number;
  comment: string | null;
  createdAt: string;
}

export const reviewService = (client: AxiosInstance) => ({
  submitReview: (data: SubmitReviewRequest) =>
    client.post<{ reviewId: string }>('/api/reviews', data).then((r) => r.data),

  getByJob: (jobId: string) =>
    client.get<ReviewDto[]>(`/api/reviews/job/${jobId}`).then((r) => r.data),

  getByUser: (userId: string) =>
    client.get<ReviewDto[]>(`/api/reviews/user/${userId}`).then((r) => r.data),

  // "My Reviews" page — every review the current user has received, enriched with
  // reviewer name + job reference/title (the raw entity carries IDs only).
  getReceived: () =>
    client.get<MyReviewDto[]>('/api/reviews/received').then((r) => r.data),
});

// ─────────────────────────────────────────────
//  PAYMENT SERVICE
// ─────────────────────────────────────────────
export interface PaymentReprocessResult {
  paymentId: string;
  carrierId: string;
  earningsPatched: boolean;
  oldEarnings: number;
  newEarnings: number;
  notificationSent: boolean;
  message: string;
}

export interface EarningsDriftEntry {
  carrierId: string;
  storedEarnings: number;
  computedEarnings: number;
  delta: number;
}

export interface EarningsReconciliationReport {
  ranAt: string;
  carriersChecked: number;
  carriersPatched: number;
  drifts: EarningsDriftEntry[];
}

export const paymentService = (client: AxiosInstance) => ({
  getDashboard: () =>
    client.get<CarrierEarningsDashboardDto>('/api/earnings/dashboard').then((r) => r.data),

  getMonthlyStatement: (year: number, month: number) =>
    client.get<MonthlyStatementDto>('/api/earnings/statement', { params: { year, month } })
      .then((r) => r.data),

  getAllPayments: () =>
    client.get<AdminPaymentsQueueDto>('/api/payments/all').then((r) => r.data),

  reprocessPayment: (paymentId: string) =>
    client.post<PaymentReprocessResult>(`/api/payments/${paymentId}/reprocess-release`)
      .then((r) => r.data),

  reconcileEarnings: () =>
    client.post<EarningsReconciliationReport>('/api/payments/reconcile-earnings')
      .then((r) => r.data),
});

export const referralService = (client: AxiosInstance) => ({
  getAllReferrals: () =>
    client.get<AdminReferralOverviewDto>('/api/referrals/all').then((r) => r.data),
});

// ─────────────────────────────────────────────
//  PROFILE SERVICE
// ─────────────────────────────────────────────
export const profileService = (client: AxiosInstance) => ({
  getProfile: () =>
    client.get<UserProfileDto>('/api/v1/users/me/profile').then(r => r.data),

  updateProfile: (data: UpdateProfileRequest) =>
    client.put<UserProfileDto>('/api/v1/users/me/profile', data).then(r => r.data),

  submitIdentityDocument: (documentType: string, documentNumber?: string) =>
    client.post<UserProfileDto>('/api/v1/users/me/identity-document', { documentType, documentNumber }).then(r => r.data),

  requestCredit: () =>
    client.post<UserProfileDto>('/api/v1/users/me/credit-request').then(r => r.data),

  uploadAvatar: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return client.post<UserProfileDto>('/api/v1/users/me/avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  deleteAccount: () => client.delete('/api/v1/users/me'),
});

// ─────────────────────────────────────────────
//  CHATBOT SERVICE
// ─────────────────────────────────────────────
export const chatbotService = (client: AxiosInstance) => ({
  sendMessage: (data: ChatbotMessageRequest) =>
    client.post<ChatbotMessageResponse>('/api/chatbot/message', data).then((r) => r.data),
});

// ─────────────────────────────────────────────
//  SUPPORT SERVICE
// ─────────────────────────────────────────────
export const supportService = (client: AxiosInstance) => ({
  submitRequest: (subject: string, message: string) =>
    client.post<{ message: string }>('/api/v1/support/request', { subject, message }).then((r) => r.data),
});

// ─────────────────────────────────────────────
//  SUGGESTIONS / FEEDBACK SERVICE
// ─────────────────────────────────────────────
export interface SuggestionDto {
  suggestionId: string;
  userRole: string;
  category: 'Feature' | 'Accessibility' | 'Bug' | 'Other';
  title: string;
  description: string;
  status: 'New' | 'UnderReview' | 'Planned' | 'Implemented' | 'Declined';
  adminNotes: string | null;
  createdAt: string;
  reviewedAt: string | null;
  fullName: string | null;
}

export const suggestionService = (client: AxiosInstance) => ({
  submit: (category: string, title: string, description: string) =>
    client.post<SuggestionDto>('/api/v1/suggestions', { category, title, description }).then((r) => r.data),
  getMine: () =>
    client.get<SuggestionDto[]>('/api/v1/suggestions/my').then((r) => r.data),
  getAllAdmin: (status?: number, page = 1, pageSize = 50) =>
    client.get<SuggestionDto[]>('/api/v1/suggestions/admin', { params: { status, page, pageSize } }).then((r) => r.data),
  updateStatus: (id: string, status: number, adminNotes?: string) =>
    client.patch(`/api/v1/suggestions/admin/${id}/status`, { status, adminNotes }),
});

// ─────────────────────────────────────────────
//  MAPS SERVICE
// ─────────────────────────────────────────────
export const mapsService = (client: AxiosInstance) => ({
  getRoute: (fromLat: number, fromLng: number, toLat: number, toLng: number) =>
    client.post<RouteResponse>('/api/maps/route', { fromLat, fromLng, toLat, toLng })
      .then((r) => r.data),

  suggestRelaySegments: (jobId: string) =>
    client.get(`/api/maps/relay-segments/${jobId}`).then((r) => r.data),

  // Live-traffic-aware ETA (TomTom, server-side) - for active tracking only. Deliberately
  // separate from getRoute: that one hits the free self-hosted Valhalla planning route and is
  // safe to call often; this one is metered/paid, so callers should throttle it themselves.
  getLiveRoute: (fromLat: number, fromLng: number, toLat: number, toLng: number) =>
    client.get<LiveRouteResponse>('/api/maps/live-route', { params: { fromLat, fromLng, toLat, toLng } })
      .then((r) => r.data),
});

// ─────────────────────────────────────────────
//  JOB ALERTS SERVICE
// ─────────────────────────────────────────────
export const jobAlertsService = (client: AxiosInstance) => ({
  getAll: () =>
    client.get<JobAlertPreference[]>('/api/v1/job-alerts').then(r => r.data),

  create: (data: CreateJobAlertRequest) =>
    client.post<JobAlertPreference>('/api/v1/job-alerts', data).then(r => r.data),

  toggle: (id: string) =>
    client.patch<JobAlertPreference>(`/api/v1/job-alerts/${id}/toggle`).then(r => r.data),

  remove: (id: string) =>
    client.delete(`/api/v1/job-alerts/${id}`),
});

// ─────────────────────────────────────────────
//  TRAFFIC NEWS & WEATHER SERVICE
//  Dashboard "Local Info" — both proxied through the backend (TrafficNewsController/
//  WeatherController) rather than fetched directly from National Highways/Met Office, since
//  those upstreams are keyed, non-browser-oriented APIs (see api.ts of the mobile app for the
//  same rationale — this mirrors that implementation exactly).
// ─────────────────────────────────────────────
export const trafficNewsService = (client: AxiosInstance) => ({
  getIncidents: (lat: number, lng: number, radiusMiles = 25) =>
    client.get<TrafficIncident[]>('/api/v1/traffic-news', { params: { lat, lng, radiusMiles } }).then(r => r.data),
  getIncidentsAlongRoute: (routePoints: { lat: number; lng: number }[], corridorMiles = 5) =>
    client.post<TrafficIncident[]>('/api/v1/traffic-news/route', { routePoints, corridorMiles }).then(r => r.data),
});

export const weatherService = (client: AxiosInstance) => ({
  getHourly: (lat: number, lng: number) =>
    client.get<HourlyWeather[]>('/api/v1/weather/hourly', { params: { lat, lng } }).then(r => r.data),
});

// ─────────────────────────────────────────────
//  DOCUMENTS SERVICE
// ─────────────────────────────────────────────
export const documentsService = (client: AxiosInstance) => ({
  getMy: () =>
    client.get<DocumentUpload[]>('/api/v1/documents/my').then(r => r.data),

  /**
   * vehicleId scopes a document to one vehicle - used by the manual MOT fallback so approval
   * verifies exactly that vehicle rather than every vehicle the uploader owns.
   */
  upload: async (file: File, documentType: DocumentType, userRole: string, expiryDate?: string, vehicleId?: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentType', documentType);
    form.append('userRole', userRole);
    if (expiryDate) form.append('expiryDate', expiryDate);
    if (vehicleId) form.append('vehicleId', vehicleId);
    const r = await client.post<DocumentUpload>('/api/v1/documents/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
});

// ─────────────────────────────────────────────
//  ELIGIBILITY SERVICE  (Carrier / Workshop)
// ─────────────────────────────────────────────
export interface QuoteEligibilityResult {
  isReferred: boolean;
  hasQualifyingRating: boolean;
  highestApprovedRating: number | null;
  hasSecurityDeposit: boolean;
  hasPendingDeposit: boolean;
  isDepositRefundUnlocked: boolean;
  hasActiveVehicles: boolean;
  allVehiclesVerified: boolean;
  isEligible: boolean;
}

export const eligibilityService = (client: AxiosInstance) => ({
  getMyEligibility: () =>
    client.get<QuoteEligibilityResult>('/api/v1/eligibility/me').then(r => r.data),

  enterReferralCode: (referralCode: string) =>
    client.post<{ message: string }>('/api/v1/eligibility/referral-code', { referralCode }).then(r => r.data),

  createDepositCheckout: () =>
    client.post<{ url: string }>('/api/v1/eligibility/deposit-checkout').then(r => r.data),
});

// ─────────────────────────────────────────────
//  AUTO-QUOTE SERVICE
//  The provider's rate card, the simulator that exercises it, and the confirm/
//  decline actions for quotes held in ConfirmFirst mode.
// ─────────────────────────────────────────────
export const autoQuoteService = (client: AxiosInstance) => ({
  /** Returns null when the provider has never set a card up (the API 404s). */
  getRule: () =>
    client
      .get<AutoQuoteRuleDto>('/api/v1/auto-quote/rule')
      .then(r => r.data)
      .catch(err => {
        if (err?.response?.status === 404) return null;
        throw err;
      }),

  saveRule: (data: SaveAutoQuoteRuleRequest) =>
    client.put<AutoQuoteRuleDto>('/api/v1/auto-quote/rule', data).then(r => r.data),

  deleteRule: () => client.delete('/api/v1/auto-quote/rule'),

  /** Prices a hypothetical job. Persists nothing and makes no routing call. */
  preview: (data: AutoQuotePreviewRequest) =>
    client.post<AutoQuotePreviewDto>('/api/v1/auto-quote/preview', data).then(r => r.data),

  confirmQuote: (quoteId: string) =>
    client.post(`/api/v1/auto-quote/quotes/${quoteId}/confirm`),

  declineQuote: (quoteId: string) =>
    client.post(`/api/v1/auto-quote/quotes/${quoteId}/decline`),

  getStandardServices: () =>
    client.get<WorkshopStandardServiceDto[]>('/api/v1/auto-quote/standard-services').then(r => r.data),

  saveStandardService: (data: SaveWorkshopStandardServiceRequest) =>
    client.put('/api/v1/auto-quote/standard-services', data),

  deleteStandardService: (serviceId: string) =>
    client.delete(`/api/v1/auto-quote/standard-services/${serviceId}`),
});

// ─────────────────────────────────────────────
//  PROVIDER AVAILABILITY SERVICE
// ─────────────────────────────────────────────
export const availabilityService = (client: AxiosInstance) => ({
  /** Blocks in a window. The API caps the range at 400 days. */
  get: (from: string, to: string) =>
    client.get<AvailabilityDto[]>('/api/v1/availability', { params: { from, to } }).then(r => r.data),

  add: (data: AddAvailabilityRequest) =>
    client.post<AvailabilityDto>('/api/v1/availability', data).then(r => r.data),

  remove: (availabilityId: string) =>
    client.delete(`/api/v1/availability/${availabilityId}`),

  /** Blocks every bank holiday in a year at once. */
  blockBankHolidays: (year: number, region = 'england-and-wales') =>
    client.post<{ added: number }>('/api/v1/availability/bank-holidays', { year, region }).then(r => r.data),
});

// ─────────────────────────────────────────────
//  DELIVERY ESTIMATE SERVICE
// ─────────────────────────────────────────────
export const deliveryEstimateService = (client: AxiosInstance) => ({
  /** Up to 40 listings per call. Returns one result per item, in the order sent. */
  batch: (items: DeliveryEstimateItem[]) =>
    client
      .post<DeliveryEstimateResult[]>('/api/v1/delivery-estimate/batch', { items })
      .then(r => r.data),
});

// ─────────────────────────────────────────────
//  SIGNALR CONNECTION FACTORY
// ─────────────────────────────────────────────
export const createSignalRConnection = (
  hubPath: string,
  getToken: () => Promise<string>,
  transport?: signalR.HttpTransportType
) =>
  new signalR.HubConnectionBuilder()
    // Prefix with VITE_API_BASE_URL so production builds reach the API backend,
    // not the static web app host (which returns 405 on /hubs/* negotiate).
    .withUrl((import.meta.env.VITE_API_BASE_URL ?? '') + hubPath, {
      accessTokenFactory: getToken,
      ...(transport !== undefined ? { transport } : {}),
    })
    .withAutomaticReconnect([0, 2000, 5000, 10_000, 30_000])
    .configureLogging(
      import.meta.env.DEV
        ? signalR.LogLevel.Information
        : signalR.LogLevel.Warning
    )
    .build();

// ─────────────────────────────────────────────
//  PAYMENTS API  (demo-token bearer per call)
// ─────────────────────────────────────────────
const authHdr = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const messagesApi = {
  getUnreadPerCarrier: (jobId: string, token: string) =>
    api.get<{ carrierId: string; unreadCount: number }[]>(
      `/messages/job/${jobId}/unread-per-carrier`,
      authHdr(token)
    ).then(r => r.data),
};

export const paymentsApi = {
  getEarningsDashboard: (token: string) =>
    api.get<EarningsDashboardDto>('/earnings/dashboard', authHdr(token)).then(r => r.data),

  getReferralDashboard: (token: string) =>
    api.get<ReferralDashboardDto>('/referrals/dashboard', authHdr(token)).then(r => r.data),
};

// ─────────────────────────────────────────────
//  CUSTOMER PAYMENTS API
// ─────────────────────────────────────────────
export interface CustomerJobPaymentSummaryItem {
  jobId: string;
  jobReference: string;
  jobTitle: string;
  carrierName: string;
  carrierId: string | null;
  scheduledAt: string;
  totalAmount: number;
  onlineAmount: number | null;
  codAmount: number | null;
  paymentStatus: number | null;
  stripePaymentIntentId: string | null;
  paidAt: string | null;
  jobStatus: number;
}

export interface CustomerPaymentSummaryDto {
  jobs: CustomerJobPaymentSummaryItem[];
  totalJobsValue: number;
  totalOnlinePaid: number;
  totalCodOwed: number;
}

export interface CustomerSplitCheckoutDto {
  checkoutUrl: string;
  jobId: string;
  onlineAmount: number;
  codAmount: number;
}

export interface BatchJobSplitRequest {
  jobId: string;
  onlineAmount: number;
  codAmount: number;
}

export interface BatchSplitCheckoutDto {
  checkoutUrl: string;
  totalOnlineAmount: number;
  jobs: BatchJobSplitRequest[];
}

export const customerPaymentsApi = {
  getSummary: (token: string) =>
    api.get<CustomerPaymentSummaryDto>('/payments/customer/summary', authHdr(token)).then(r => r.data),

  createSplitCheckout: (jobId: string, onlineAmount: number, codAmount: number, token: string) =>
    api.post<CustomerSplitCheckoutDto>(
      '/payments/customer/split-checkout',
      { jobId, onlineAmount, codAmount },
      authHdr(token)
    ).then(r => r.data),

  confirmPayment: (jobId: string, token: string) =>
    api.post('/payments/customer/confirm', { jobId }, authHdr(token)).then(r => r.data),

  createBatchSplitCheckout: (jobs: BatchJobSplitRequest[], token: string) =>
    api.post<BatchSplitCheckoutDto>(
      '/payments/customer/batch-split-checkout',
      { jobs },
      authHdr(token)
    ).then(r => r.data),

  batchConfirmPayment: (jobIds: string[], token: string) =>
    api.post('/payments/customer/batch-confirm', { jobIds }, authHdr(token)).then(r => r.data),
};

// ─────────────────────────────────────────────
//  REPORTING API  (admin, demo-token bearer per call)
// ─────────────────────────────────────────────
export const reportingApi = {
  getDashboard: (token: string) =>
    api.get<PlatformKpiSnapshot>('/admin/reporting/dashboard', authHdr(token)).then(r => r.data),

  getMonthlyHistory: (token: string, months: number) =>
    api.get<MonthlyKpiRow[]>('/admin/reporting/monthly', { ...authHdr(token), params: { months } }).then(r => r.data),

  getDailyTrend: (from: Date, to: Date, token: string) =>
    api.get<DailyRevenueTick[]>('/admin/reporting/daily-trend', {
      ...authHdr(token),
      params: { from: from.toISOString(), to: to.toISOString() },
    }).then(r => r.data),

  getTopCarriers: (from: Date, to: Date, token: string) =>
    api.get<TopCarrierRow[]>('/admin/reporting/top-carriers', {
      ...authHdr(token),
      params: { from: from.toISOString(), to: to.toISOString() },
    }).then(r => r.data),
};

// ─────────────────────────────────────────────
//  DISPUTES API
// ─────────────────────────────────────────────
export const disputeService = (client: AxiosInstance) => ({
  getMine: () =>
    client.get<DisputeDto[]>('/api/disputes/mine').then(r => r.data),

  getSummary: () =>
    client.get<DisputeSummaryDto>('/api/disputes/summary').then(r => r.data),

  open: (data: {
    jobId: string; raisedByParty: number;
    category: number; title: string; description: string; evidenceUrls?: string[];
  }) =>
    client.post<DisputeDto>('/api/disputes', data).then(r => r.data),

  addUpdate: (disputeId: string, data: {
    authorParty: number; message: string; attachmentUrls?: string[];
  }) =>
    client.post<DisputeDto>(`/api/disputes/${disputeId}/update`, data).then(r => r.data),

  uploadEvidence: (images: Array<{ base64: string; mimeType: string }>) =>
    client.post<{ urls: string[] }>('/api/disputes/evidence', { images }).then(r => r.data.urls),

  respondToResolution: (disputeId: string, accepted: boolean) =>
    client.post(`/api/disputes/${disputeId}/respond-resolution`, { accepted }).then(r => r.data),

  getQueue: (page = 1, pageSize = 50) =>
    client.get<DisputeDto[]>('/api/disputes/queue', { params: { page, pageSize } }).then(r => r.data),

  proposeResolution: (disputeId: string, data: { resolutionType: number; refundAmount?: number; explanation: string }) =>
    client.post(`/api/disputes/${disputeId}/propose-resolution`, data).then(r => r.data),

  assign: (disputeId: string, agentUserId: string) =>
    client.post(`/api/disputes/${disputeId}/assign`, { agentUserId }).then(r => r.data),

  escalate: (disputeId: string) =>
    client.post(`/api/disputes/${disputeId}/escalate`).then(r => r.data),
});

// ─────────────────────────────────────────────
//  NOTIFICATIONS API
// ─────────────────────────────────────────────
export const notificationsApi = {
  getMine: (token: string) =>
    api.get<NotificationDto[]>('/notifications', authHdr(token)).then(r => r.data),

  getUnreadCount: (token: string) =>
    api.get<{ count: number }>('/notifications/unread-count', authHdr(token)).then(r => r.data),

  getPrefs: (token: string) =>
    api.get<NotificationPreference>('/notifications/preferences', authHdr(token)).then(r => r.data),

  markRead: (id: string, token: string) =>
    api.post(`/notifications/${id}/read`, {}, authHdr(token)),

  markAllRead: (token: string) =>
    api.post('/notifications/read-all', {}, authHdr(token)),

  savePrefs: (prefs: Partial<NotificationPreference>, token: string) =>
    api.put<NotificationPreference>('/notifications/preferences', prefs, authHdr(token)).then(r => r.data),

  isJobMuted: (jobId: string, token: string) =>
    api.get<{ muted: boolean }>(`/notifications/jobs/${jobId}/muted`, authHdr(token)).then(r => r.data.muted),

  muteJob: (jobId: string, token: string) =>
    api.post(`/notifications/jobs/${jobId}/mute`, {}, authHdr(token)),

  unmuteJob: (jobId: string, token: string) =>
    api.delete(`/notifications/jobs/${jobId}/mute`, authHdr(token)),
};

// ─────────────────────────────────────────────
//  DVLA LICENCE VERIFICATION SERVICE
//  Driver submits their DVLA share code; Ops/Admin opens GOV.UK
//  with the code, uploads the resulting PDF, and marks the driver
//  as Verified. Drivers see their details read-only.
// ─────────────────────────────────────────────
export const dvlaService = (client: AxiosInstance) => ({
  // Driver submits their GOV.UK share code; Ops/Admin will use it to verify
  submitShareCode: (shareCode: string) =>
    client.post('/api/v1/dvla/share-code', { shareCode }).then(r => r.data),

  // Driver reads their own DVLA status (read-only)
  getStatus: () =>
    client.get<DvlaStatusResponse>('/api/v1/dvla/status').then(r => r.data),

  getHistory: () =>
    client.get<DvlaStatusResponse[]>('/api/v1/dvla/history').then(r => r.data),

  getPdfUrl: (blobUri: string) =>
    `/api/v1/dvla/pdf?blobUri=${encodeURIComponent(blobUri)}`,

  // Admin/Ops: list drivers with pending share codes awaiting processing
  adminGetPending: () =>
    client.get<DvlaPendingItem[]>('/api/v1/dvla/admin/pending').then(r => r.data),

  // Admin/Ops: upload the GOV.UK PDF for a specific driver and parse it
  adminProcess: (userId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return client.post<DvlaUploadResponse>(`/api/v1/dvla/admin/${userId}/process`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
});

export interface DvlaCategoryResponse {
  category: string;
  startDate: string | null;
  endDate: string | null;
  codes: string | null;
}

export interface DvlaUploadResponse {
  parseSuccess: boolean;
  parseFailureReason: string | null;
  carrierFullName: string | null;
  checkCode: string | null;
  licenceNumber: string | null;
  licenceIssueNumber: number | null;
  licenceValidFrom: string | null;
  licenceValidTo: string | null;
  drivingStatus: string | null;
  endorsementOffences: number;
  endorsementPoints: number;
  dateSummaryViewed: string | null;
  canDriveCategories: DvlaCategoryResponse[];
  provisionalCategories: DvlaCategoryResponse[];
  pdfBlobUri: string | null;
  nextCheckDue: string;
}

export interface DvlaStatusResponse {
  verified: boolean;
  uploadedAt: string | null;
  nextCheckDue: string | null;
  isOverdue: boolean;
  carrierFullName: string | null;
  licenceNumber: string | null;
  licenceIssueNumber: number | null;
  licenceValidFrom: string | null;
  licenceValidTo: string | null;
  drivingStatus: string | null;
  endorsementOffences: number;
  endorsementPoints: number;
  dateSummaryViewed: string | null;
  parseSuccess: boolean;
  pdfBlobUri: string | null;
}

export interface DvlaPendingItem {
  userId: string;
  carrierFullName: string;
  email: string;
  shareCode: string;
  submittedAt: string;
}

// ─────────────────────────────────────────────
//  STRIPE CONNECT API
// ─────────────────────────────────────────────

export interface PaymentVerificationItem {
  paymentId: string;
  jobId: string;
  carrierId: string;
  jobReferenceNumber: string;
  jobTitle: string;
  jobPaymentTerm: number;
  customerFullName: string;
  carrierFullName: string;
  carrierEmail: string;
  carrierSortCode: string | null;
  carrierAccountNumber: string | null;
  carrierStripeAccountId: string | null;
  carrierStripeOnboarded: boolean;
  grossAmount: number;
  commissionDeducted: number;
  cashbackDeducted: number;
  referralDeducted: number;
  netAmount: number;
  paymentStatus: number;
  payoutMethod: number;
  stripePaymentIntentId: string | null;
  stripeTransferId: string | null;
  opsApprovedByUserId: string | null;
  opsApprovedAt: string | null;
  releasedAt: string | null;
  createdAt: string;
  failureReason: string | null;
  podPhotoUrl: string | null;
  podSignatureUrl: string | null;
  podRecipientName: string | null;
  podCapturedAt: string | null;
}

export const stripeConnectApi = {
  getVerificationQueue: (token: string, page = 1, pageSize = 50) =>
    api.get<PaymentVerificationItem[]>(
      '/stripe-connect/payment-verification-queue',
      { ...authHdr(token), params: { page, pageSize } }
    ).then(r => r.data),

  opsApprovePayment: (paymentId: string, opsUserId: string, token: string) =>
    api.post(`/payments/${paymentId}/ops-approve`, { opsUserId }, authHdr(token)).then(r => r.data),

  adminReleasePayment: (paymentId: string, adminId: string, token: string) =>
    api.post(`/payments/${paymentId}/release`, { adminId }, authHdr(token)).then(r => r.data),

  advancePaymentToProcessing: (paymentId: string, token: string) =>
    api.post(`/payments/${paymentId}/advance-to-processing`, {}, authHdr(token)).then(r => r.data),

  startExpressOnboarding: (token: string) =>
    api.post<{ onboardingUrl: string; accountId: string }>(
      '/stripe-connect/onboard', {}, authHdr(token)
    ).then(r => r.data),

  getOnboardingStatus: (token: string) =>
    api.get<{
      onboarded: boolean; detailsSubmitted: boolean;
      chargesEnabled: boolean; accountId: string | null; accountType: string | null;
    }>('/stripe-connect/status', authHdr(token)).then(r => r.data),

  registerBankAccount: (data: {
    fullName: string; sortCode: string; accountNumber: string;
    dobDay: string; dobMonth: string; dobYear: string;
    addressLine1: string; city: string; postcode: string;
  }, token: string) =>
    api.post<{ accountId: string; onboarded: boolean }>(
      '/stripe-connect/bank-account', data, authHdr(token)
    ).then(r => r.data),
};

// ─────────────────────────────────────────────
//  EMPLOYEE PORTAL SERVICE
//  Authenticated by a magic-link token (no MSAL). Token is passed as
//  Bearer header directly — no user sign-in required.
// ─────────────────────────────────────────────
const portalSessionKey = (token: string) => `portal_session_${token.slice(-16)}`;

export const createEmployeePortalClient = (portalToken: string) => {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${portalToken}`,
    },
  });

  // Replay the session ID claimed on first open so refreshes don't break auth.
  client.interceptors.request.use((config) => {
    const sessionId = localStorage.getItem(portalSessionKey(portalToken));
    if (sessionId) config.headers['X-Session-Id'] = sessionId;
    return config;
  });

  client.interceptors.response.use(
    (res) => {
      const sid = res.headers['x-session-id'];
      if (sid) localStorage.setItem(portalSessionKey(portalToken), sid);
      return res;
    },
    (err) => {
      const message = err.response?.data?.detail
        ?? err.response?.data?.error
        ?? err.message
        ?? 'An error occurred';
      return Promise.reject(new Error(message));
    }
  );
  return client;
};

export const employeePortalService = (portalToken: string) => {
  const client = createEmployeePortalClient(portalToken);
  return {
    getJobs: () =>
      client.get<JobSummaryDto[]>('/api/employee-portal/jobs').then((r) => r.data),

    getJob: (jobId: string) =>
      client.get<JobDetailDto>(`/api/employee-portal/jobs/${jobId}`).then((r) => r.data),

    updateIdleLocation: (lat: number, lng: number, accuracy?: number) =>
      client.post('/api/employee-portal/location', { lat, lng, accuracy }),

    updateLocation: (jobId: string, lat: number, lng: number, accuracy?: number) =>
      client.post(`/api/employee-portal/jobs/${jobId}/location`, { lat, lng, accuracy }),

    startJob: (jobId: string) =>
      client.post(`/api/employee-portal/jobs/${jobId}/start`),

    completeJob: (jobId: string) =>
      client.post(`/api/employee-portal/jobs/${jobId}/complete`),

    uploadImages: (jobId: string, images: Array<{ base64: string; mimeType: string }>) =>
      client.post<{ urls: string[] }>(`/api/employee-portal/jobs/${jobId}/images`, { images })
        .then((r) => r.data),

    createRepairDraft: (data: {
      vehicleRegistration: string;
      issueDescription: string;
      locationAddress: string;
      pickupLat: number;
      pickupLng: number;
      images: Array<{ base64: string; mimeType: string }>;
    }) =>
      client.post<{ jobId: string }>('/api/employee-portal/repair-drafts', data).then(r => r.data),

    submitPod: (jobId: string, data: {
      photoUrl: string;
      customerSignatureUrl: string;
      recipientName: string;
      gpsLatitude: number;
      gpsLongitude: number;
      gpsAccuracyMetres: number;
      otpCode: string;
    }) =>
      client.post<{ podId: string }>(`/api/employee-portal/jobs/${jobId}/pod`, data)
        .then((r) => r.data),
  };
};

export interface LiveCarrierLocationDto {
  carrierId: string;
  carrierName: string;
  jobId: string;
  jobReference: string;
  latitude: number;
  longitude: number;
  accuracyMetres?: number;
  recordedAt: string;
}

export interface EmployeePortalTokenInfo {
  carrierId: string;
  createdAt: string;
  lastUsedAt: string | null;
  isActive: boolean;
}

// ─────────────────────────────────────────────
//  CARRIER FLEET SERVICE
// ─────────────────────────────────────────────
export const vehiclesService = (client: AxiosInstance) => ({
  /** Step 1: ask DVSA about a plate. Persists nothing. */
  lookup: (registration: string) =>
    client.post<VehicleLookupResult>('/api/v1/vehicles/lookup', { registration }).then(r => r.data),

  getAll: () =>
    client.get<UserVehicle[]>('/api/v1/vehicles').then(r => r.data),

  /** Step 2: the user confirmed the DVSA details are their vehicle. */
  registerVehicle: (input: RegisterVehicleInput) =>
    client.post<UserVehicle>('/api/v1/vehicles', input).then(r => r.data),

  getVehicle: (vehicleId: string) =>
    client.get<UserVehicle>(`/api/v1/vehicles/${vehicleId}`).then(r => r.data),

  updateVehicle: (vehicleId: string, input: UpdateVehicleInput) =>
    client.put<UserVehicle>(`/api/v1/vehicles/${vehicleId}`, input).then(r => r.data),

  deactivateVehicle: (vehicleId: string) =>
    client.delete(`/api/v1/vehicles/${vehicleId}`),

  /** Stored MOT tests, newest first, each with its defects. */
  getMotHistory: (vehicleId: string) =>
    client.get<MotTest[]>(`/api/v1/vehicles/${vehicleId}/mot-history`).then(r => r.data),

  /** Re-poll DVSA now, for a user who has just had an MOT done. */
  refreshMot: (vehicleId: string) =>
    client.post<UserVehicle>(`/api/v1/vehicles/${vehicleId}/refresh-mot`).then(r => r.data),
});

/** @deprecated Vehicles are no longer carrier-only. Use vehiclesService. */
export const carrierFleetService = vehiclesService;

// ─────────────────────────────────────────────
//  CARRIER EMPLOYEES SERVICE
// ─────────────────────────────────────────────
export const carrierEmployeesService = (client: AxiosInstance) => ({
  getAll: () =>
    client.get<CarrierEmployee[]>('/api/carrier/employees').then(r => r.data),

  add: (input: RegisterEmployeeInput) =>
    client.post<CarrierEmployee>('/api/carrier/employees', input).then(r => r.data),

  remove: (id: string) =>
    client.delete(`/api/carrier/employees/${id}`),

  generateMagicLink: (id: string) =>
    client.post<{ token: string; url: string }>(`/api/carrier/employees/${id}/magic-link`).then(r => r.data),

  revokeMagicLink: (id: string) =>
    client.delete(`/api/carrier/employees/${id}/magic-link`),

  updateExpiry: (id: string, insuranceExpiryDate: string | null, licenceExpiryDate: string | null) =>
    client.patch(`/api/carrier/employees/${id}/expiry`, { insuranceExpiryDate, licenceExpiryDate }),

  updateEmployee: (id: string, input: { fullName: string; mobile?: string; address?: string }) =>
    client.patch<CarrierEmployee>(`/api/carrier/employees/${id}`, input).then(r => r.data),
});

// ─────────────────────────────────────────────
//  WORKSHOP EMPLOYEES SERVICE
// ─────────────────────────────────────────────
export const workshopEmployeesService = (client: AxiosInstance) => ({
  getAll: () =>
    client.get<WorkshopEmployee[]>('/api/workshop/employees').then(r => r.data),

  add: (input: RegisterWorkshopEmployeeInput) =>
    client.post<WorkshopEmployee>('/api/workshop/employees', input).then(r => r.data),

  remove: (id: string) =>
    client.delete(`/api/workshop/employees/${id}`),
});

// ─────────────────────────────────────────────
//  JOB ASSIGNMENT SERVICE
// ─────────────────────────────────────────────
export const jobAssignmentService = (client: AxiosInstance) => ({
  get: (jobId: string) =>
    client.get<JobAssignment>(`/api/jobs/${jobId}/assignment`).then(r => r.data),

  assign: (jobId: string, data: { employeeId?: string; vehicleId?: string }) =>
    client.post<JobAssignment>(`/api/jobs/${jobId}/assignment`, data).then(r => r.data),
});

// ─────────────────────────────────────────────
//  FAVOURITE JOBS SERVICE
// ─────────────────────────────────────────────
export const favouriteJobsService = (client: AxiosInstance) => ({
  add: (jobId: string) =>
    client.post(`/api/v1/favourite-jobs/${jobId}`).then(r => r.data),

  remove: (jobId: string) =>
    client.delete(`/api/v1/favourite-jobs/${jobId}`).then(r => r.data),

  getAll: () =>
    client.get<FavouriteJobSummary[]>('/api/v1/favourite-jobs').then(r => r.data),

  getAvailable: () =>
    client.get<AvailableJobsResponse>('/api/v1/favourite-jobs/available').then(r => r.data),
});

// Reads a File as a base64 payload for the /media JSON-body upload endpoints
// (backend expects { items: [{ base64, mimeType, name }] }, not multipart).
function fileToUploadItem(file: File): Promise<{ base64: string; mimeType: string; name: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.slice(result.indexOf(',') + 1);
      resolve({ base64, mimeType: file.type || 'application/octet-stream', name: file.name });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// ─────────────────────────────────────────────
//  BAZAAR SERVICE
// ─────────────────────────────────────────────
export const bazaarService = (client: AxiosInstance) => ({
  browse: (params: BazaarSearchParams) =>
    client.get<BazaarListing[]>('/api/v1/bazaar', { params }).then(r => r.data),

  // Passing page + pageSize flips the API response to the paginated shape — see BazaarController.Browse.
  browsePaged: (params: BazaarSearchParams & { page: number; pageSize: number }) =>
    client.get<BazaarBrowseResult>('/api/v1/bazaar', { params }).then(r => r.data),

  get: (id: string) =>
    client.get<BazaarListing>(`/api/v1/bazaar/${id}`).then(r => r.data),

  getMine: () =>
    client.get<BazaarListing[]>('/api/v1/bazaar/mine').then(r => r.data),

  // Public "View Shop" — a seller's other active listings.
  getBySeller: (sellerId: string) =>
    client.get<BazaarListing[]>(`/api/v1/bazaar/seller/${sellerId}`).then(r => r.data),

  create: (data: CreateBazaarListingInput) =>
    client.post<{ listingId: string }>('/api/v1/bazaar', data).then(r => r.data),

  cancel: (id: string) =>
    client.delete(`/api/v1/bazaar/${id}`).then(r => r.data),

  markSold: (id: string) =>
    client.post(`/api/v1/bazaar/${id}/sold`).then(r => r.data),

  uploadMedia: async (files: File[]) => {
    const items = await Promise.all(files.map(fileToUploadItem));
    return client.post<{ urls: string[]; media: ListingMedia[] }>('/api/v1/bazaar/media', { items })
      .then(r => r.data.media);
  },

  aiSuggest: (data: BazaarAiSuggestInput) =>
    client.post<AiSuggestResult>('/api/v1/bazaar/ai-suggest', data).then(r => r.data),

  getQuotes: (listingId: string) =>
    client.get<BazaarQuote[]>(`/api/v1/bazaar/${listingId}/quotes`).then(r => r.data),

  submitQuote: (listingId: string, data: SubmitBazaarQuoteInput) =>
    client.post<BazaarQuote>(`/api/v1/bazaar/${listingId}/quotes`, data).then(r => r.data),

  acceptQuote: (listingId: string, quoteId: string) =>
    client.post<{ orderId: string }>(`/api/v1/bazaar/${listingId}/quotes/${quoteId}/accept`).then(r => r.data),

  rejectQuote: (listingId: string, quoteId: string) =>
    client.post(`/api/v1/bazaar/${listingId}/quotes/${quoteId}/reject`).then(r => r.data),

  getMyOrders: () =>
    client.get<BazaarOrder[]>('/api/v1/bazaar/orders/mine').then(r => r.data),

  getModerationQueue: () =>
    client.get<ModerationQueueItem[]>('/api/v1/admin/bazaar/moderation').then(r => r.data),

  moderate: (listingId: string, action: 'approve' | 'reject', reason?: string) =>
    client.post(`/api/v1/admin/bazaar/${listingId}/moderation`, { action, reason }).then(r => r.data),

  addFavourite: (listingId: string) =>
    client.post(`/api/v1/bazaar-favourites/${listingId}`).then(r => r.data),

  removeFavourite: (listingId: string) =>
    client.delete(`/api/v1/bazaar-favourites/${listingId}`).then(r => r.data),

  getFavourites: () =>
    client.get<BazaarFavouriteSummary[]>('/api/v1/bazaar-favourites').then(r => r.data),
});

// ─────────────────────────────────────────────
//  BAZAAR MESSAGES SERVICE (listing-scoped chat)
// ─────────────────────────────────────────────
export const bazaarMessagesService = (client: AxiosInstance) => ({
  getMessages: (listingId: string, otherPartyId?: string) =>
    client.get<BazaarChatMessage[]>(`/api/v1/bazaar/${listingId}/messages`, { params: { otherPartyId } }).then(r => r.data),

  sendMessage: (listingId: string, content: string, recipientId?: string) =>
    client.post<BazaarChatMessage>(`/api/v1/bazaar/${listingId}/messages`, { content, recipientId }).then(r => r.data),

  markAllRead: (listingId: string, otherPartyId: string) =>
    client.patch(`/api/v1/bazaar/${listingId}/messages/read-all`, null, { params: { otherPartyId } }).then(r => r.data),
});

// ─────────────────────────────────────────────
//  BAZAAR ORDERS SERVICE (Buy Now / checkout)
// ─────────────────────────────────────────────
export const bazaarOrdersService = (client: AxiosInstance) => ({
  create: (data: CreateBazaarOrderInput) =>
    client.post<CreateBazaarOrderResult>('/api/v1/bazaar/orders', data).then(r => r.data),

  getById: (orderId: string) =>
    client.get<BazaarOrder>(`/api/v1/bazaar/orders/${orderId}`).then(r => r.data),

  confirmPayment: (orderId: string, paymentIntentId: string) =>
    client.post<BazaarOrder>(`/api/v1/bazaar/orders/${orderId}/confirm`, { paymentIntentId }).then(r => r.data),

  getOtp: (orderId: string) =>
    client.get<{ otp: string }>(`/api/v1/bazaar/orders/${orderId}/payment-otp`).then(r => r.data),

  codReceived: (orderId: string, otpCode: string) =>
    client.post<BazaarOrder>(`/api/v1/bazaar/orders/${orderId}/cod-received`, { otpCode }).then(r => r.data),

  dispute: (orderId: string, reason: string) =>
    client.post(`/api/v1/bazaar/orders/${orderId}/dispute`, { reason }).then(r => r.data),

  linkDeliveryJob: (orderId: string, itemId: string, jobId: string) =>
    client.post(`/api/v1/bazaar/orders/${orderId}/items/${itemId}/delivery-job`, { jobId }).then(r => r.data),

  setFulfilmentMethod: (orderId: string, itemId: string, method: BazaarFulfilmentMethod) =>
    client.post(`/api/v1/bazaar/orders/${orderId}/items/${itemId}/fulfilment-method`, { method }).then(r => r.data),
});

// ─────────────────────────────────────────────
//  BAZAAR SHIPMENTS SERVICE (ShipEngine: rate shopping, label purchase/void, tracking)
// ─────────────────────────────────────────────
export const bazaarShipmentsService = (client: AxiosInstance) => ({
  getRates: (orderId: string, itemId: string, shipFrom: ShippingAddressInput, shipTo: ShippingAddressInput) =>
    client.post<ShippingRateQuote[]>(
      `/api/v1/bazaar/orders/${orderId}/items/${itemId}/shipment/rates`, { shipFrom, shipTo }
    ).then(r => r.data),

  purchaseLabel: (orderId: string, itemId: string, rateId: string) =>
    client.post<BazaarShipment>(
      `/api/v1/bazaar/orders/${orderId}/items/${itemId}/shipment/label`, { rateId }
    ).then(r => r.data),

  voidLabel: (orderId: string, itemId: string) =>
    client.post<BazaarShipment>(`/api/v1/bazaar/orders/${orderId}/items/${itemId}/shipment/void`).then(r => r.data),

  get: (orderId: string, itemId: string) =>
    client.get<BazaarShipment>(`/api/v1/bazaar/orders/${orderId}/items/${itemId}/shipment`).then(r => r.data),
});

// ─────────────────────────────────────────────
//  CLASSIFIED SERVICE
// ─────────────────────────────────────────────
export const classifiedService = (client: AxiosInstance) => ({
  browse: (params: ClassifiedSearchParams) =>
    client.get<ClassifiedListing[]>('/api/v1/classified', { params }).then(r => r.data),

  get: (id: string) =>
    client.get<ClassifiedListing>(`/api/v1/classified/${id}`).then(r => r.data),

  getMine: () =>
    client.get<ClassifiedListing[]>('/api/v1/classified/mine').then(r => r.data),

  create: (data: CreateClassifiedListingInput) =>
    client.post<CreateClassifiedListingResult>('/api/v1/classified', data).then(r => r.data),

  confirmPayment: (listingId: string, paymentIntentId: string) =>
    client.post<ClassifiedListing>(`/api/v1/classified/${listingId}/confirm-payment`, { paymentIntentId }).then(r => r.data),

  cancel: (id: string) =>
    client.delete(`/api/v1/classified/${id}`).then(r => r.data),

  markSold: (id: string) =>
    client.post(`/api/v1/classified/${id}/sold`).then(r => r.data),

  uploadMedia: async (files: File[]) => {
    const items = await Promise.all(files.map(fileToUploadItem));
    return client.post<{ urls: string[]; media: ListingMedia[] }>('/api/v1/classified/media', { items })
      .then(r => r.data.media);
  },

  aiSuggest: (data: Omit<BazaarAiSuggestInput, 'listingType'>) =>
    client.post<AiSuggestResult>('/api/v1/classified/ai-suggest', data).then(r => r.data),

  getModerationQueue: () =>
    client.get<ModerationQueueItem[]>('/api/v1/admin/classified/moderation').then(r => r.data),

  moderate: (listingId: string, action: 'approve' | 'reject', reason?: string) =>
    client.post(`/api/v1/admin/classified/${listingId}/moderation`, { action, reason }).then(r => r.data),

  addFavourite: (listingId: string) =>
    client.post(`/api/v1/classified-favourites/${listingId}`).then(r => r.data),

  removeFavourite: (listingId: string) =>
    client.delete(`/api/v1/classified-favourites/${listingId}`).then(r => r.data),

  getFavourites: () =>
    client.get<ClassifiedFavouriteSummary[]>('/api/v1/classified-favourites').then(r => r.data),
});

// Public (unauthenticated) client for Bazaar/Classified browse/get — same baseURL as createApiClient
// but no auth header. Used so unauthenticated visitors can browse.
const _publicClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  headers: { 'Content-Type': 'application/json' },
});
_publicClient.interceptors.response.use(
  (res) => res,
  (err) => {
    const message = err.response?.data?.detail ?? err.response?.data?.error ?? err.message ?? 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

export const publicBazaarService = {
  browse: (params: BazaarSearchParams) =>
    _publicClient.get<BazaarListing[]>('/api/v1/bazaar', { params }).then(r => r.data),
  browsePaged: (params: BazaarSearchParams & { page: number; pageSize: number }) =>
    _publicClient.get<BazaarBrowseResult>('/api/v1/bazaar', { params }).then(r => r.data),
  get: (id: string) =>
    _publicClient.get<BazaarListing>(`/api/v1/bazaar/${id}`).then(r => r.data),
  getBySeller: (sellerId: string) =>
    _publicClient.get<BazaarListing[]>(`/api/v1/bazaar/seller/${sellerId}`).then(r => r.data),
};

export const publicClassifiedService = {
  browse: (params: ClassifiedSearchParams) =>
    _publicClient.get<ClassifiedListing[]>('/api/v1/classified', { params }).then(r => r.data),
  get: (id: string) =>
    _publicClient.get<ClassifiedListing>(`/api/v1/classified/${id}`).then(r => r.data),
};

// ─────────────────────────────────────────────
//  INVOICES API
// ─────────────────────────────────────────────
export const invoicesService = (client: AxiosInstance) => ({
  getMine: () => client.get<InvoiceDto[]>('/api/v1/invoices/mine').then(r => r.data),
  getForMe: () => client.get<InvoiceDto[]>('/api/v1/invoices/for-me').then(r => r.data),
  getById: (id: string) => client.get<InvoiceDto>(`/api/v1/invoices/${id}`).then(r => r.data),
  create: (data: CreateInvoiceRequest) => client.post<InvoiceDto>('/api/v1/invoices', data).then(r => r.data),
  approve: (id: string) => client.post<InvoiceDto>(`/api/v1/invoices/${id}/approve`).then(r => r.data),
  // Customer-only, and takes no address: the API always sends to the address on the invoice.
  emailToMe: (id: string) =>
    client.post<{ sentTo: string }>(`/api/v1/invoices/${id}/email-to-me`).then(r => r.data),
  cancel: (id: string) => client.delete(`/api/v1/invoices/${id}`).then(r => r.data),
  downloadPdf: (id: string) =>
    client.get(`/api/v1/invoices/${id}/pdf`, { responseType: 'blob' }).then(r => URL.createObjectURL(r.data)),
  getSettings: () => client.get<InvoiceSettingsDto>('/api/v1/invoices/settings').then(r => r.data),
  updateSettings: (data: UpdateInvoiceSettingsRequest) =>
    client.put<InvoiceSettingsDto>('/api/v1/invoices/settings', data).then(r => r.data),
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('logo', file);
    return client.post<{ logoUrl: string }>('/api/v1/invoices/settings/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.logoUrl);
  },
});

// ─────────────────────────────────────────────
//  PLATFORM FEE CONFIG API
// ─────────────────────────────────────────────
/** The active wildcard-role rule for a fee — the row every role falls back to. */
const fallbackRule = (rules: PlatformFeeRule[], feeCode: string) =>
  rules.find(r => r.feeCode === feeCode && r.roleId === null && r.isActive);

export const platformFeeConfigService = (client: AxiosInstance) => ({
  /** Every rule, active or not — the admin grid shows them all. */
  getRules: () => client.get<PlatformFeeRule[]>('/api/v1/platform/fee-config').then(r => r.data),

  /**
   * The headline rates, derived from the rules, for screens that only want to *show* a
   * customer what the platform charges. Anything that actually charges money must let the
   * server resolve it — the rate a given seller pays depends on their role and channel.
   */
  getEffective: () =>
    client.get<PlatformFeeRule[]>('/api/v1/platform/fee-config').then(r => {
      const rules = r.data;
      return {
        bazaarDepositPercent:         fallbackRule(rules, 'BAZAAR_DEPOSIT_MIN')?.rate ?? 0.25,
        bazaarSellerFeePercent:       fallbackRule(rules, 'BAZAAR_SALE')?.rate ?? 0.05,
        deliveryJobCommissionPercent: fallbackRule(rules, 'DELIVERY_COMMISSION')?.rate ?? 0.15,
        repairJobCommissionPercent:   fallbackRule(rules, 'REPAIR_COMMISSION')?.rate ?? 0.15,
        classifiedMonthlyFee:         fallbackRule(rules, 'CLASSIFIED_LISTING')?.flatAmount ?? 10,
        walletTopUpFeePercent:        rules.find(x => x.feeCode === 'WALLET_TOPUP'
                                        && x.channelCode === 'STRIPE_CONNECT' && x.isActive)?.rate ?? 0.02,
      };
    }),

  /** id 0 inserts a new rule, otherwise updates the existing one. */
  upsertRule: (data: UpsertPlatformFeeRuleInput) =>
    client.put('/api/v1/platform/fee-config', data).then(r => r.data),
});

// ─────────────────────────────────────────────
//  WALLET API
// ─────────────────────────────────────────────
export const walletService = (client: AxiosInstance) => ({
  get: () => client.get<WalletAccountDto>('/api/v1/wallet').then(r => r.data),
  getStatement: (limit = 100) =>
    client.get<WalletLedgerEntryDto[]>(`/api/v1/wallet/statement?limit=${limit}`).then(r => r.data),
  getMyTopUps: () => client.get<WalletTopUpDto[]>('/api/v1/wallet/top-ups').then(r => r.data),
  requestTopUp: (amount: number, method: WalletTopUpMethod) =>
    client.post<CreateWalletTopUpResponse>('/api/v1/wallet/top-ups', { amount, method }).then(r => r.data),
  confirmTopUp: (topUpId: string, paymentIntentId: string) =>
    client.post(`/api/v1/wallet/top-ups/${topUpId}/confirm`, { paymentIntentId }).then(r => r.data),

  // Ops: bank-transfer approval queue
  getPendingTopUps: () =>
    client.get<PendingWalletTopUpDto[]>('/api/v1/wallet/top-ups/pending').then(r => r.data),
  approveTopUp: (topUpId: string) =>
    client.post(`/api/v1/wallet/top-ups/${topUpId}/approve`).then(r => r.data),
  rejectTopUp: (topUpId: string, reason: string) =>
    client.post(`/api/v1/wallet/top-ups/${topUpId}/reject`, { reason }).then(r => r.data),
});

// ─────────────────────────────────────────────
//  REGISTRATION DEPOSIT (card vs free bank transfer)
// ─────────────────────────────────────────────
export const depositService = (client: AxiosInstance) => ({
  getQuote: () =>
    client.get<DepositQuoteDto>('/api/v1/eligibility/deposit-quote').then(r => r.data),
  createCardCheckout: () =>
    client.post<{ url: string }>('/api/v1/eligibility/deposit-checkout').then(r => r.data),
  declareBankTransfer: () =>
    client.post<DepositBankTransferInstructionDto>('/api/v1/eligibility/deposit-bank-transfer').then(r => r.data),
});

// ─────────────────────────────────────────────
//  BAZAAR IDENTITY VERIFICATION API
// ─────────────────────────────────────────────
export const identityVerificationService = (client: AxiosInstance) => ({
  getStatus: () => client.get<IdentityVerificationStatusDto>('/api/v1/identity-verification/status').then(r => r.data),
  submit: (verificationType: IdentityVerificationType, file: File) => {
    const form = new FormData();
    form.append('verificationType', String(verificationType));
    form.append('file', file);
    return client.post<IdentityVerificationDto>('/api/v1/identity-verification', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  getPending: () => client.get<IdentityVerificationDto[]>('/api/v1/admin/identity-verifications/pending').then(r => r.data),
  approve: (id: string) => client.post(`/api/v1/admin/identity-verifications/${id}/approve`).then(r => r.data),
  reject: (id: string, notes: string) =>
    client.post(`/api/v1/admin/identity-verifications/${id}/reject`, { notes }).then(r => r.data),
});

// ─────────────────────────────────────────────
//  BAZAAR REVIEWS API (My Reviews - Bazaar tab)
// ─────────────────────────────────────────────
export const bazaarCategoryService = (client: AxiosInstance) => ({
  getTree: () => client.get<BazaarCategory[]>('/api/v1/bazaar/categories').then(r => r.data),

  /**
   * The attributes a category asks for, including everything inherited from its parent and
   * filtered to the surface asking. Drives both the create form and the filter sheet, so a
   * new category attribute reaches users without a client release.
   */
  getAttributes: (categoryId: string, surface: 'bazaar' | 'classified') =>
    client
      .get<ListingCategoryAttribute[]>(
        `/api/v1/listing-categories/${categoryId}/attributes?surface=${surface}`)
      .then(r => r.data),
});

export const bazaarReviewService = (client: AxiosInstance) => ({
  getReceived: () => client.get<MyBazaarReviewDto[]>('/api/v1/bazaar-reviews/received').then(r => r.data),
  getForSeller: (sellerId: string) =>
    client.get<BazaarSellerReviewSummary>(`/api/v1/bazaar-reviews/seller/${sellerId}`).then(r => r.data),
});

export { createApiClient };
